import React, { useEffect } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Autocomplete, TextField } from '@mui/material';
import ProgressBar from '../../../../Shared/ProgressBar';
import { ReactComponent as HubIcon } from '../../../../../images/icons/assign_user.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import { getFacilityDevices } from '../../../../../store/facility/actionCreators';
import { getDeviceList } from '../../../../../store/devices/actionCreators';
import {
  borderGrey,
  borderGreyFocused,
  lightBlack,
  lightGrey,
} from '../../../../../mainTheme';

interface HubAssignmentProps {
  onSubmit: (hubId: number) => void;
  onBack: () => void;
  facilityId: number | null;
  isFromModernFacility?: boolean;
  showProgressBar?: boolean;
}

export const HubAssignment: React.FC<HubAssignmentProps> = ({
  onSubmit,
  onBack,
  facilityId,
  isFromModernFacility = false,
  showProgressBar = true,
}) => {
  const dispatch = useDispatch();
  const [selectedHub, setSelectedHub] = React.useState<any>(null);

  const { facilityDevices, isLoading: isFacilityLoading } = useSelector(
    (state: RootState) => state.facility,
  );
  const { deviceList, isLoading: isDeviceLoading } = useSelector(
    (state: RootState) => state.devices,
  );

  useEffect(() => {
    if (facilityId) {
      dispatch(getFacilityDevices(facilityId, isFromModernFacility));
    } else {
      dispatch(getDeviceList());
    }
  }, [facilityId, dispatch, isFromModernFacility]);

  const availableHubs = React.useMemo(() => {
    if (facilityId && facilityDevices) {
      return facilityDevices.devices
        .filter((device) => !device.owner)
        .map((device) => ({
          id: device.id,
          name: device.serial_number,
        }));
    }

    if (!facilityId && deviceList) {
      return deviceList
        .filter((device) => !device.facility_id && !device.customer_id)
        .map((device) => ({
          id: device.id,
          name: device.serial_number,
        }));
    }

    return [];
  }, [facilityId, facilityDevices, deviceList]);

  if (isFacilityLoading || isDeviceLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {showProgressBar && <ProgressBar currentStep={2} totalSteps={2} />}
      <HubIcon />
      <Typography>Assign a Hub</Typography>

      <Autocomplete
        options={availableHubs}
        getOptionLabel={(option) => option.name}
        onChange={(_event, newValue) => setSelectedHub(newValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Hub serial number"
            placeholder="Hub serial number"
            fullWidth
            sx={{
              '& .MuiInputBase-input': {
                color: lightBlack,
                fontSize: '16px',
              },
              '& .MuiInputLabel-root': {
                color: `${lightGrey} !important`,
                fontSize: '16px',
              },
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '& fieldset': {
                  borderColor: borderGrey,
                  borderRadius: '8px',
                },
                '&:hover fieldset': {
                  borderColor: borderGrey,
                },
                '&.Mui-focused fieldset': {
                  borderColor: borderGreyFocused,
                },
              },
              '.MuiAutocomplete-endAdornment': {
                top: '50%',
                transform: 'translateY(-50%)',
              },
              '.MuiAutocomplete-clearIndicator': {
                '& svg': {
                  width: 20,
                  height: 20,
                },
              },
            }}
          />
        )}
        popupIcon={
          <FontAwesomeIcon
            icon={faAngleDown}
            style={{
              width: '20px',
              height: '20px',
            }}
            color="#9999A3"
          />
        }
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.id}
            style={{
              color: lightBlack,
              padding: '8px 16px',
              fontSize: '16px',
            }}
          >
            <span style={{ fontWeight: 500 }}>{option.name}</span>
          </li>
        )}
        ListboxProps={{
          style: {
            border: `1px solid ${borderGrey}`,
            borderRadius: '8px',
            marginTop: '14px',
            boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.4)', // match CustomerCareSelect
          },
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          variant="text"
          color="secondary"
          onClick={onBack}
          sx={{
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '18px',
            letterSpacing: '0.2px',
            height: '44px',
            width: '180px',
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => selectedHub && onSubmit(Number(selectedHub.id))}
          disabled={!selectedHub}
          sx={{
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '18px',
            letterSpacing: '0.2px',
            height: '44px',
            width: '180px',
          }}
        >
          Add
        </Button>
      </Box>
    </Box>
  );
};

export default HubAssignment;
